// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Event attendance data model from SQL query (API returns uppercase keys)
 */
export interface EventAttendanceData {
  EVENT_NAME: string;
  EVENT_START_DATE: string;
  USER_ID: string;
  USER_FULL_NAME: string;
  USER_EMAIL: string;
  USER_PHOTO_URL?: string;
  USER_TITLE: string;
  PROJECT_NAME: string;
  PROJECT_SLUG: string;
  FOUNDATION_NAME: string;
}

/**
 * Event attendance summary data model from SQL query
 */
export interface EventAttendanceSummaryData {
  totalEvents: number;
}

/**
 * Entity service for event attendance metric type
 */
@Injectable({
  providedIn: 'root'
})
export class EventAttendanceMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: 'Count of LF events for this project\'s foundation.',
    columns: [
      { name: 'Our event attenders', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Event name', prop: 'eventName' },
      { name: 'Event date', prop: 'eventDate' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LFX'
  };

  /**
   * Get columns for event attendance table
   * Based on UI requirements for event attendance display
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for event attendance
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/event-attendance`;
  }

  /**
   * Fetch event attendance data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<EventAttendanceData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event attendance data');
            }

            return response.data.map(attendance => this.transformToDataRow(attendance));
          }),
          catchError(error => {
            console.error('Error fetching event attendance data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in event attendance entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform event attendance data to DataRow format for the table
   */
  private transformToDataRow(attendance: EventAttendanceData): DataRow {
    return {
      displayName: {
        text: attendance.USER_FULL_NAME,
        extraData: {
          imageUrl: attendance.USER_PHOTO_URL,
          userId: attendance.USER_ID,
          memberDisplayName: attendance.USER_FULL_NAME,
          memberLogoUrl: attendance.USER_PHOTO_URL
        }
      },
      eventName: { text: attendance.EVENT_NAME },
      eventDate: { text: this.formatDate(attendance.EVENT_START_DATE) }
    };
  }

  /**
   * Fetch event attendance summary data from backend
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: EventAttendanceSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch event attendance summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: response.data.totalEvents.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching event attendance summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in event attendance summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}

